import React, { useEffect, useState } from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';
import CSSModule from 'react-css-modules';
import { Heart, Tag } from 'react-feather';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { COUPON_TYPES } from 'coupon-common';
import { actions as followActions } from 'coupon-common/src/modules/businesses/follow';
import { dispatchAsAsync } from 'coupon-common/src/utils';

import { RouteNames } from '../../constants';
import NoImage from '../../assets/images/NoCouponImage.webp';
import ImageSlider from '../ImageSlider/ImageSlider';
import styles from './CustomerCouponCard.module.scss';

/**
 * DESIGN UPDATE (grid — original): markup/styling ported to match the
 * marketing site's card language (rounded-16px corners, image-on-top with
 * overlay badges, red-gradient discount pill, Poppins type). Still built
 * on react-css-modules/SCSS — see CustomerCouponCard.module.scss.
 *
 * DESIGN UPDATE (list — new): `layout="list"` renders the horizontal card
 * built from HorizontalCoupon.svg — image left (1:1 square), badges
 * stacked over it, business logo+name / title / description in the body,
 * and a bottom-right row with expiry + deal-amount + a dedicated Redeem
 * button. That source file had no text layers (Figma export with only
 * chrome shapes), so the exact colors/positions/gradients were read
 * directly from its rect/gradient coordinates, but text placement and
 * sizing is a judgment call, kept consistent with the grid card's type
 * scale — flag anything that should shift once compared against the
 * actual design file.
 *
 * `hideBusinessHeader` (new): suppresses the logo+name(+Follow) row
 * entirely. Added for contexts like BusinessesDetails, where every card on
 * the page already belongs to the one business shown elsewhere — repeating
 * its name/logo per card would be redundant. Applies to both layouts.
 *
 * FOLLOW BUTTON — shows only while the business is NOT followed. There is
 * no "Following" label state at all: once handleToggleFollow() succeeds
 * and isFollowing flips to true (via the followedResults selector below),
 * the button unmounts entirely on both layouts.
 *
 * isFollowing comparison is coerced to String() on both sides —
 * followedResults' business_id (from the follow-list endpoint) and this
 * card's businessId (from the coupon's embedded business object) can come
 * back as different types (number vs string) even when they represent the
 * same id, since they're two different API responses. Strict === was
 * silently always failing in that mismatched case, which is why a card
 * could fail to reflect a follow made elsewhere even though the redux
 * store had genuinely updated.
 *
 * Same data shape/handlers drive both layouts; only the markup/CSS branch
 * differs. No data-derivation logic duplicated elsewhere should re-derive
 * dealAmount/originalPrice/etc — this component is the single source for
 * that now (see BusinessesDetails.js, which used to have its own copies).
 *
 * The "photo still loading" treatment (LoadingBGImage + pulsing MiniLogo)
 * lives inside ImageSlider itself, per-slide, since that's where the
 * actual load state is known. This component only decides between "has
 * images → render ImageSlider" and "no images at all → NoCouponImage".
 *
 * BUSINESS LOGO FALLBACK: when the business has no logo on file,
 * businessLogo is falsy and both layouts render a letter-avatar (first
 * character of businessName, uppercased) in place of the <img> instead of
 * a broken image icon — styled with the same sizing as the real logo
 * (.business-logo-fallback / .list-logo-fallback in the stylesheet) so the
 * header layout doesn't shift depending on whether a logo exists.
 *
 * HEART BURST (favourite animation): clicking the heart button, when it
 * results in the item becoming favourited (not un-favouriting), also
 * mounts a large centered heart glyph over the coupon image that pops in,
 * settles, then fades — the classic Instagram double-tap-like burst. It's
 * driven by its own `heartBurst` boolean state, independent of
 * `localFavourite`, so it can be reset/re-triggered on every "like" without
 * being tangled up in the favourite toggle logic itself. The node
 * unmounts itself via onAnimationEnd so a rapid unlike→like sequence
 * re-mounts (and thus re-plays) the animation instead of no-op'ing on an
 * already-mounted element. Shared by both layouts since both wrap their
 * image area in a `position: relative` container already used by
 * heart-button's own absolute positioning.
 *
 * LOGIN GATE (favourite + follow) — this card now determines authorization
 * itself (`state.auth.authorized`, same flag CouponAction.js/
 * CouponsDetails.js/BusinessesDetails.js already read) instead of relying
 * on a parent-supplied `isGuest`/`onGuestAction` prop. That prop only ever
 * got wired up on one screen (GuestHome); every other embedder (Home,
 * BusinessesDetails) had no equivalent gating at all, so a signed-out
 * visitor there could hit the heart or Follow button and dispatch straight
 * into a 401. Since this card is shared across multiple pages, checking
 * `authorized` internally means every embedder gets the same gate for
 * free with no per-page wiring. On a miss, both handlers open
 * `isLoginPromptVisible` — a single self-contained modal (inline-styled,
 * not styleName-based, since CustomerCouponCard.module.scss doesn't define
 * any modal classes) portaled to document.body so position: fixed isn't
 * thrown off by this card's own hover-scale transform. Portaled content
 * still bubbles clicks through the *React* tree, not the DOM tree, so its
 * own buttons/overlay call stopPropagation() the same way the heart/follow
 * buttons already do to keep clicks from reaching the card's onPress.
 *
 * AFFILIATE DISCLOSURE (new): when `is_affiliate_or_flyer` is true, a
 * small disclosure line renders directly below the primary action for
 * each layout — the Redeem button (list) / the clickable footer block
 * (grid). Plain text, not interactive, so it sits outside those buttons
 * rather than inside them.
 */
const CustomerCouponCard = ({
  data,
  onPress,
  goToBusinessDetails,
  toggleFavourite,
  isFavourite,
  first,
  layout,
  hideBusinessHeader,
  business: businessOverride,
  cardRef,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [localFavourite, setLocalFavourite] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);
  const [heartBurst, setHeartBurst] = useState(false);
  const [isLoginPromptVisible, setIsLoginPromptVisible] = useState(false);

  const authorized = useSelector(state => !!(state.auth && state.auth.authorized));

  const {
    description,
    type,
    points_back: pointsBack,
    amount_off: amountOff,
    expiry_type: expiryType,
    title,
    expiry,
    is_sponsored: isSponsored,
    published_at: publishedAt,    
    percentage_off: percentageOff,
    image_url: imageUrls,
    is_exclusive_followers: isExclusiveFollowers,
    is_exclusive_paid: isExclusivePaid,
    is_affiliate_or_flyer: isAffiliateOrFlyer,
    // ASSUMPTION: guessing the real field name/shape here — confirm against
    // your actual coupon schema and adjust this one line if it's wrong
    // (e.g. category_name, coupon_category, category.title, etc).
    category,
    business: businessDataFromCoupon,

  } = data;

  const businessData = businessOverride || businessDataFromCoupon;
  const businessId = businessData && businessData.id;
  const followedResults = useSelector(state => (state.businesses.follow.data && state.businesses.follow.data.results) || []);
  // Coerced to String() on both sides — see file-level comment for why.
  const isFollowing = Array.isArray(followedResults)
    ? followedResults.some(b => String(b.business_id) === String(businessId))
    : false;

  const publishedAtParsed = publishedAt && new Date(publishedAt);
  const isNew = publishedAtParsed && (new Date() - publishedAtParsed) / (1000 * 60 * 60 * 24) <= 6;

  let dealAmount;
  if (type === COUPON_TYPES.AMOUNT_OFF) dealAmount = `$${amountOff} off`;
  else if (type === COUPON_TYPES.PERCENTAGE_OFF) dealAmount = `${Math.round(percentageOff)}% off`;
  else if (type === COUPON_TYPES.POINTS_BACK) dealAmount = `${pointsBack} points`;

  // ASSUMPTION: matches the same guessed field used on the BusinessesDetails
  // deal cards (getOriginalPrice there, before that logic moved here) — a
  // struck-through "was" price shown next to the deal-amount pill. Only
  // renders when `original_price` is a number; confirm the real field name
  // against your API, or remove this if there's no such concept server-side.
  const { original_price: originalPriceValue } = data;
  const originalPrice = typeof originalPriceValue === 'number' ? `$${originalPriceValue}` : null;

  let formattedExpiry = '';
  if (expiryType === 'date' && expiry) {
    const parsed = new Date(expiry);
    if (!isNaN(parsed.getTime())) {
      formattedExpiry = new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(parsed);
    }
  }

  const currentFavourite = localFavourite !== null ? localFavourite : isFavourite;

  const handleToggleFavourite = () => {
    if (!authorized) {
      setIsLoginPromptVisible(true);
      return;
    }

    const nextFavourite = !currentFavourite;
    setLocalFavourite(nextFavourite);
    toggleFavourite();
    // Only burst on like, not unlike — matches Instagram's behavior of
    // never showing the heart pop when removing a like.
    if (nextFavourite) {
      setHeartBurst(true);
    }
  };

  const handleToggleFollow = () => {
    if (!authorized) {
      setIsLoginPromptVisible(true);
      return;
    }

    const businessObject = {
      ...businessData,
      business_id: businessId,
      is_affiliate_or_flyer: !!isAffiliateOrFlyer,
    };

    if (isFollowing) {
      const followRecord = followedResults.find(b => String(b.business_id) === String(businessId));
      dispatchAsAsync(
        followActions.unfollowBusinessRequest({
          id: followRecord && followRecord.id,
          business_id: businessId,
          is_affiliate_or_flyer: !!isAffiliateOrFlyer,
        }),
        dispatch,
      );
    } else {
      dispatchAsAsync(followActions.followBusinessRequest(businessObject), dispatch);
    }
  };

  const hasImages = imageUrls && imageUrls.length > 0;
  const rawBusinessLogo = businessData && businessData.logo;
  const businessLogo = rawBusinessLogo && !logoFailed ? rawBusinessLogo : null;

  useEffect(() => {
    setLogoFailed(false);
  }, [rawBusinessLogo]);
  const businessName = businessData && businessData.name;
  const businessCity = (businessData && businessData.city && businessData.city.name) || 'Online';
  const categoryName = (category && (category.name || category.title)) || (typeof category === 'string' ? category : null);

  // First non-whitespace character of the business name, uppercased, for
  // the fallback logo avatar. Falls back to '?' if the name is
  // missing/empty so the avatar never renders blank.
  const businessInitial = ((businessName || '').trim()[0] || '?').toUpperCase();

  const badgeNodes = (
    <>
      {categoryName && <span styleName="badge">{categoryName}</span>}
      {isNew && <span styleName="badge">New</span>}
      {isExclusivePaid && <span styleName="badge badge-gold">Premium</span>}
      {isExclusiveFollowers && <span styleName="badge badge-gold">Exclusive</span>}
      {isSponsored && <span styleName="badge">Sponsored</span>}
    </>
  );

  const heartButtonNode = (
    <span
      role="button"
      tabIndex={0}
      styleName="heart-button"
      onClick={e => {
        e.stopPropagation();
        handleToggleFavourite();
      }}
      onKeyPress={e => {
        if (e.key === 'Enter') {
          e.stopPropagation();
          handleToggleFavourite();
        }
      }}
    >
      <Heart size={16} fill={currentFavourite ? '#e0245e' : 'none'} color={currentFavourite ? '#e0245e' : '#9b9b9b'} />
    </span>
  );

  // Instagram-style heart burst — large heart glyph centered over the
  // coupon image, pops in and fades out. Mounted only while `heartBurst`
  // is true; removes itself once the CSS animation finishes so the next
  // "like" (a fresh mount) can replay it from scratch.
  const heartBurstNode = heartBurst && (
    <span styleName="heart-burst" onAnimationEnd={() => setHeartBurst(false)}>
      <Heart size={72} fill="#fff" color="#fff" />
    </span>
  );

  // Follow button — only rendered while NOT following. No "Following"
  // label state exists; once isFollowing flips true, this returns null
  // and the button disappears entirely (both layouts share this).
  const followButtonNode = !isFollowing && (
    <span
      role="button"
      tabIndex={0}
      styleName="follow-text"
      onClick={e => {
        e.stopPropagation();
        handleToggleFollow();
      }}
      onKeyPress={e => {
        if (e.key === 'Enter') {
          e.stopPropagation();
          handleToggleFollow();
        }
      }}
    >
      Follow
    </span>
  );

  // Affiliate/flyer disclosure — plain text, not clickable, so it lives
  // outside the primary action button rather than nested inside it.
  const affiliateDisclosureNode = isAffiliateOrFlyer && (
    <p styleName="affiliate-disclosure">
      My Couponbook may earn a commission when you buy through links on our site.
    </p>
  );

  // Self-contained login prompt — see LOGIN GATE in the file header for
  // why this lives here (inline-styled, portaled) rather than as
  // styleName/CSS-module markup like the rest of the card.
  const loginPromptNode = isLoginPromptVisible && createPortal(
    <div
      role="presentation"
      onClick={e => {
        e.stopPropagation();
        setIsLoginPromptVisible(false);
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 4000,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Log in required"
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: '28px 24px',
          width: '100%',
          maxWidth: 360,
          textAlign: 'center',
          fontFamily: "'Poppins', Helvetica, sans-serif",
        }}
      >
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px', color: '#171717' }}>Log in to continue</h2>
        <p style={{ fontSize: 14, color: '#737373', margin: '0 0 20px' }}>
          Create a free account (or log in) to save favourites and follow your favourite businesses.
        </p>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            setIsLoginPromptVisible(false);
            navigate(RouteNames.SIGN_IN);
          }}
          style={{
            width: '100%',
            padding: '12px 0',
            borderRadius: 999,
            border: 'none',
            background: 'linear-gradient(90deg, #ef4444, #7f1d1d)',
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Log In
        </button>
      </div>
    </div>,
    document.body,
  );

  // ─────────────────────────── LIST LAYOUT ───────────────────────────
  if (layout === 'list') {
    return (
      <>
        <div
          ref={cardRef}
          styleName="list-card"
          className={cx({ 'mt-0': first })}
          role="button"
          tabIndex={0}
          onClick={onPress}
          onKeyPress={e => {
            if (e.key === 'Enter') onPress();
          }}
        >
          <div styleName="list-image-wrap">
            {hasImages ? (
              <ImageSlider images={imageUrls.map(img => img.image_url)} showNavs={false} showBullets={false} altText={title} />
            ) : (
              <img src={NoImage} alt="No image available" styleName="no-image-placeholder" />
            )}
            <div styleName="list-image-vignette" />
            <div styleName="list-badges-col">{badgeNodes}</div>
            {heartButtonNode}
            {heartBurstNode}
          </div>

          <div styleName="list-body">
            <div styleName="list-top-row">
              {!hideBusinessHeader && (
                <button
                  type="button"
                  styleName="list-header-row"
                  onClick={e => {
                    e.stopPropagation();
                    goToBusinessDetails();
                  }}
                >
                  {businessLogo ? (
                    <span styleName="list-logo-ring">
                      <img src={businessLogo} alt={businessName || ''} onError={() => setLogoFailed(true)} />
                    </span>
                  ) : (
                    <span styleName="list-logo-fallback" aria-label={businessName || 'Business logo'}>
                      {businessInitial}
                    </span>
                  )}
                  <span styleName="list-business-name">{businessName}</span>
                </button>
              )}

              {!hideBusinessHeader && followButtonNode}

              {expiry && expiryType === 'date' && formattedExpiry && (
                <span styleName="list-expiry">Expires : {formattedExpiry}</span>
              )}
            </div>

            {!hideBusinessHeader && businessCity && (
              <p styleName="list-location">{businessCity === 'Remote' ? 'Online' : businessCity}</p>
            )}

            <h3 styleName="list-title">{title}</h3>
            {description && <p styleName="list-description">{description}</p>}

            <div styleName="list-footer-row">
              <div styleName="list-price-group">
                {originalPrice && <span styleName="list-original-price">{originalPrice}</span>}
                {dealAmount && <span styleName="list-deal-amount">{dealAmount}</span>}
              </div>
              <button
                type="button"
                styleName="list-redeem-btn"
                onClick={e => {
                  e.stopPropagation();
                  onPress();
                }}
              >
                <Tag size={14} />
                Redeem
              </button>
            </div>
          </div>
        </div>
        {loginPromptNode}
      </>
    );
  }

  // ─────────────────────────── GRID LAYOUT (default, unchanged) ───────────────────────────
  return (
    <>
      <div ref={cardRef} styleName="card" className={cx({ 'mt-0': first })}>
        <button type="button" styleName="image-wrap" onClick={onPress}>
          <div styleName={isAffiliateOrFlyer ? 'affiliate-image-container' : 'image-container'}>
            {hasImages ? (
              <ImageSlider images={imageUrls.map(img => img.image_url)} showNavs showBullets altText={title} />
            ) : (
              <img src={NoImage} alt="No image available" styleName="no-image-placeholder" />
            )}
          </div>

          <div styleName="badges-row">{badgeNodes}</div>

          {heartButtonNode}
          {heartBurstNode}
        </button>

        <div styleName="body">
          {!hideBusinessHeader && (
            <button type="button" styleName="business-header" onClick={goToBusinessDetails}>
              {businessLogo ? (
                <img src={businessLogo} alt={businessName || ''} styleName="business-logo" onError={() => setLogoFailed(true)} />
              ) : (
                <span styleName="business-logo-fallback" aria-label={businessName || 'Business logo'}>
                  {businessInitial}
                </span>
              )}
              <div styleName="business-info">
                <span styleName="business-name">{businessName}</span>
                <span styleName="business-city">{businessCity === 'Remote' ? 'Online' : businessCity}</span>
              </div>
              {followButtonNode}
            </button>
          )}

          <button type="button" styleName="footer" onClick={onPress}>
            <h3 styleName="title">{title}</h3>
            <p styleName="description">{description}</p>
            <div styleName="footer-bottom">
              <div styleName="price-group">
                {dealAmount ? <span styleName="deal-amount">{dealAmount}</span> : <span />}
                {originalPrice && <span styleName="original-price">{originalPrice}</span>}
              </div>
              {expiry && expiryType === 'date' && formattedExpiry && <span styleName="expiry-date">Expires {formattedExpiry}</span>}
            </div>
          </button>

        </div>
      </div>
      {loginPromptNode}
    </>
  );
};

CustomerCouponCard.propTypes = {
  data: PropTypes.object,
  onPress: PropTypes.func,
  goToBusinessDetails: PropTypes.func,
  toggleFavourite: PropTypes.func,
  isFavourite: PropTypes.bool,
  first: PropTypes.bool,
  layout: PropTypes.oneOf(['grid', 'list']),
  hideBusinessHeader: PropTypes.bool,
  business: PropTypes.object,
   cardRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

CustomerCouponCard.defaultProps = {
  data: {},
  onPress: undefined,
  goToBusinessDetails: undefined,
  toggleFavourite: undefined,
  isFavourite: false,
  first: false,
  layout: 'grid',
  hideBusinessHeader: false,
  business: null,
   cardRef: null,
};

export default CSSModule(CustomerCouponCard, styles, { allowMultiple: true });